import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import { get } from '@/features/shared/lib/api';

/**
 * MajorJournalsBar
 * - Horizontal bar chart for "List of Major Publication Journals".
 * - Props:
 *   - items: Array<{ label: string, value: number }>
 *     value is expected to be percentage (0~100). If larger, it will be clamped.
 *   - scholarId: scholar ID for API call
 */
export default function MajorJournalsBar({ items: externalItems = null, scholarId = null }) {
  const navigate = useNavigate();
  // API에서 데이터 가져오기 (externalItems가 없을 때만)
  const [journalsData, setJournalsData] = useState(null);
  const [journalsLoading, setJournalsLoading] = useState(false);
  const [journalsErr, setJournalsErr] = useState(null);

  useEffect(() => {
    // externalItems가 제공되면 API 호출하지 않음
    if (externalItems !== null) {
      return;
    }

    // scholarId가 없으면 API 호출하지 않음
    if (!scholarId) {
      return;
    }

    let alive = true;
    async function fetchJournals() {
      setJournalsLoading(true);
      setJournalsErr(null);
      try {
        const response = await get(`/rm/v1/researcher/${encodeURIComponent(scholarId)}/major_journals`);
        if (alive) {
          if (response?.status && response?.result) {
            setJournalsData(response.result);
          } else {
            setJournalsErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setJournalsData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setJournalsErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setJournalsLoading(false);
        }
      }
    }
    fetchJournals();
    return () => { alive = false; };
  }, [externalItems, scholarId]);

  // 데이터 처리: externalItems 또는 API 데이터 사용
  const items = useMemo(() => {
    if (externalItems !== null && Array.isArray(externalItems)) {
      return externalItems;
    }
    if (journalsData && Array.isArray(journalsData)) {
      return journalsData;
    }
    return null;
  }, [externalItems, journalsData]);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';

    // 로딩 중이면 그리지 않음
    if (externalItems === null && journalsLoading) {
      return;
    }

    // const fallback = [
    //   { label: 'IEEE TRANSACTIONS ON COMPUTATIONAL\nIMAGING', value: 97 },
    //   { label: 'INTERNATIONAL JOURNAL OF LOW-CARBON\nTECHNOLOGIES', value: 81 },
    //   { label: 'ANATOMICAL RECORD-ADVANCES', value: 25 },
    //   { label: 'DISCOVER NEUROSCIENCE', value: 20 },
    //   { label: 'FRONTIERS IN NEUROINFORMATICS', value: 3 },
    // ];

    const data = Array.isArray(items) && items.length ? items : [];

    // 툴팁용 div (마우스 오버 시 저널 정보 표시)
    let tooltipDiv = el.querySelector('.major-journals-tooltip');
    if (!tooltipDiv) {
      tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'major-journals-tooltip';
      Object.assign(tooltipDiv.style, {
        position: 'absolute',
        pointerEvents: 'none',
        display: 'none',
        padding: '8px 12px',
        background: '#fff',
        border: '1px solid #E4E8F1',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        fontSize: '12px',
        color: '#6C7687',
        maxWidth: '280px',
        zIndex: 1000,
        lineHeight: 1.4,
      });
      el.style.position = 'relative';
      el.appendChild(tooltipDiv);
    }

    const showTooltip = (event, d) => {
      const label = String(d.label ?? '').replace(/\n|\\n/g, ' ');
      const value = Math.round(Number(d.value) || 0);
      tooltipDiv.innerHTML = `
        <div style="font-weight:700;color:#376BDB;margin-bottom:4px;">${label}</div>
        <div>논문 수: <strong>${value}</strong>편</div>
        ${d.journal_id ? '<div style="margin-top:4px;color:#8D9AAF;font-size:11px;">클릭 시 저널 페이지로 이동</div>' : ''}
      `;
      tooltipDiv.style.display = 'block';
      const rect = el.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      tooltipDiv.style.left = `${Math.min(x + 12, rect.width - 290)}px`;
      tooltipDiv.style.top = `${Math.max(y - 8, 0)}px`;
      tooltipDiv.style.transform = 'translateY(-100%)';
    };

    const hideTooltip = () => {
      tooltipDiv.style.display = 'none';
    };

    const width = el.clientWidth || 800;
    const height = 280; // compact
    const margin = { top: 16, right: 10, bottom: 24, left: 200 };

    // 긴 라벨을 두 줄로 분할 (공백 기준)
    const wrapLabel = (str, maxLen = 28) => {
      const s = String(str ?? '').trim();
      if (s.length <= maxLen) return [s];
      const mid = Math.floor(s.length / 2);
      const before = s.lastIndexOf(' ', mid);
      const splitAt = before >= 0 ? before : mid;
      return [s.slice(0, splitAt).trim(), s.slice(splitAt).trim()].filter(Boolean);
    };

    const svg = d3
      .select(el)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // X axis max value: max of data + 10% 여유
    const maxVal = d3.max(data, d => Number(d.value) || 0) || 100;
    const xMax = Math.ceil(maxVal * 1.1);

    // Scales
    const x = d3
      .scaleLinear()
      .domain([0, xMax])
      .range([0, innerW])
      .nice();

    const y = d3
      .scaleBand()
      .domain(data.map((d) => d.label))
      .range([0, innerH])
      .padding(0.25);

    // Gridlines
    const xTicks = x.ticks(5);
    // Ensure the last value (xMax) is included if it's not already there
    const lastTick = xTicks[xTicks.length - 1];
    const finalTicks = (lastTick < xMax) ? [...xTicks, xMax] : xTicks;

    g.append('g')
      .selectAll('line')
      .data(finalTicks)
      .enter()
      .append('line')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#E4E8F1');

    // X axis labels (numbers under grid)
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(finalTicks)
          .tickFormat((d) => `${d}`)
      )
      .call((gx) => {
        gx.selectAll('.domain').remove();
        gx.selectAll('text').attr('fill', '#8D9AAF').style('font-size', 12);
        gx.selectAll('line').remove();
      });

    // Y labels on the left (긴 라벨은 두 줄로 표시)
    svg
      .append('g')
      .attr('transform', `translate(${margin.left - 10},${margin.top})`)
      .selectAll('text')
      .data(data)
      .enter()
      .append('text')
      .attr('x', -10)
      .attr('y', (d) => (y(d.label) || 0) + (y.bandwidth() / 2))
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .style('fill', '#6C7687')
      .style('font-size', 12)
      .selectAll('tspan')
      .data((d) => {
        const parts = String(d.label).split(/\n|\\n/).filter(Boolean);
        if (parts.length > 1) return parts;
        return wrapLabel(d.label);
      })
      .enter()
      .append('tspan')
      .attr('x', -10)
      .attr('dy', (_, i) => (i === 0 ? 0 : 14))
      .text((s) => s);

    // Bars
    const barColor = '#89A7F1';
    const bar = g
      .selectAll('rect')
      .data(data)
      .enter()
      .append('rect')
      .attr('x', 0)
      .attr('y', (d) => y(d.label))
      .attr('height', y.bandwidth())
      .attr('rx', 4)
      .attr('fill', barColor)
      .attr('opacity', 0.85)
      .attr('width', 0)
      .style('cursor', (d) => (d.journal_id ? 'pointer' : 'default'))
      .on('mouseover', function (event, d) {
        showTooltip(event, d);
      })
      .on('mousemove', function (event, d) {
        const rect = el.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        tooltipDiv.style.left = `${Math.min(x + 12, rect.width - 290)}px`;
        tooltipDiv.style.top = `${Math.max(y - 8, 0)}px`;
      })
      .on('mouseout', hideTooltip)
      .on('click', function (event, d) {
        if (!d.journal_id) return;
        const homeUrl = (typeof rmVars !== 'undefined' && rmVars.homeUrl) ? rmVars.homeUrl : '';
        const url = homeUrl + '/publications/?journal_id[]=' + encodeURIComponent(d.journal_id);

        // Cmd/Ctrl 키가 눌려있으면 새 탭에서 열기
        if (event.metaKey || event.ctrlKey) {
          window.open(url, '_blank');
        } else {
          navigate('/publications/?journal_id[]=' + encodeURIComponent(d.journal_id));
        }
      });

    bar
      .transition()
      .duration(600)
      .attr('width', (d) => x(Number(d.value) || 0));

    // Values at the end of bars
    g
      .selectAll('bar-label')
      .data(data)
      .enter()
      .append('text')
      .attr('x', (d) => x(Number(d.value) || 0) + 4)
      .attr('y', (d) => (y(d.label) || 0) + y.bandwidth() / 2)
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#6C7687')
      .style('font-weight', 700)
      .style('font-size', 13) // 폰트 크기를 11에서 13으로 키움
      .text((d) => `${Math.round(Number(d.value) || 0)}`);
  }, [items, externalItems, journalsLoading]);

  // 로딩 상태 처리
  if (externalItems === null && journalsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[280px] text-[#8D9AAF]">
        Loading...
      </div>
    );
  }

  // 에러 상태 처리
  if (externalItems === null && journalsErr) {
    return (
      <div className="flex items-center justify-center min-h-[280px] text-[#F3652D] text-sm">
        {journalsErr}
      </div>
    );
  }

  // 데이터가 없는 경우
  if (!items || (Array.isArray(items) && items.length === 0)) {
    return (
      <div className="flex items-center justify-center min-h-[280px] text-[#8D9AAF]">
        데이터가 없습니다.
      </div>
    );
  }

  return <div ref={ref} className="w-full" />;
}
